import React, { useEffect, useState } from "react";
import { ref, onValue, set, remove, update, get } from "firebase/database";
import { db } from "./firebase";
import StudentsTable from "./StudentsTable";
import AddStudentForm from "./AddStudentForm";
import ImportExportStudents from "./ImportExportStudents";
import { formatCourseId } from "./utils/formatCourse";

const UsersManagement = () => {
  const [courses, setCourses] = useState({});
  const [students, setStudents] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddFormExpanded, setIsAddFormExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsubscribe = onValue(coursesRef, (snapshot) => {
      const data = snapshot.val() || {};
      setCourses(data);

      // Only set the first course as active if no course is currently selected
      if (!activeCourse && Object.keys(data).length > 0) {
        setActiveCourse(Object.keys(data)[0]);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [activeCourse]); // Add activeCourse as dependency

  useEffect(() => {
    if (!activeCourse) return;
    const studentsRef = ref(db, `courses/${activeCourse}/students`);
    const studentsListener = onValue(studentsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const studentsList = Object.values(data);
      setStudents(studentsList);
    });

    return () => studentsListener();
  }, [activeCourse]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 text-gray-900 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Students Management</h1>

      {/* Course Selection */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-2 text-gray-900">Select Course</h2>
        <select
          value={activeCourse}
          onChange={(e) => setActiveCourse(e.target.value)}
          className="w-full p-2 border rounded-md text-gray-900"
        >
          {Object.keys(courses).map(course => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {/* Search and Add Student */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <input
          type="text"
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-2 border rounded-md text-gray-900"
        />
        <button
          onClick={() => setIsAddFormExpanded(!isAddFormExpanded)}
          className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {isAddFormExpanded ? "▲ Hide Add Form" : "▼ Add New Student"}
        </button>
      </div>

      {/* Add Student Form (Collapsible) */}
      {isAddFormExpanded && (
        <div className="mb-6">
          <AddStudentForm
            courses={Object.keys(courses)}
            activeCourse={activeCourse}
            onStudentAdded={() => setIsAddFormExpanded(false)}
          />
        </div>
      )}

      {/* Import/Export Section */}
      <ImportExportStudents courses={courses} activeCourse={activeCourse} />

      {/* Students Table */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <StudentsTable
          students={students}
          activeCourse={activeCourse}
          searchQuery={searchQuery}
          onDelete={() => { }}
        />
      </div>
    </div>
  );
};

export default UsersManagement;